import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { useApi, PageHead } from "@/admin/AdminApp";

const COLS = {
  hotel: [["name", "Hotel name *"], ["city", "City"], ["address", "Address"], ["map_link", "Google Maps link"], ["website", "Website"]],
  vehicle: [["cab_number", "Registration no. *"], ["vehicle", "Vehicle type"], ["driver_name", "Driver name"], ["driver_mobile", "Driver mobile"]],
};

const Row = ({ item, type, onSaved }) => {
  const api = useApi();
  const [d, setD] = useState(item?.data || {});
  const save = async () => {
    try {
      await (item ? api("put", `/directory/${item.id}`, { type, data: d }) : api("post", "/directory", { type, data: d }));
      toast.success("Saved");
      if (!item) setD({});
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => { try { await api("delete", `/directory/${item.id}`); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid items-end gap-2 border-b border-[var(--line)] px-4 py-3 last:border-0 md:grid-cols-[repeat(5,1fr)_auto]" data-testid={`dir-row-${type}-${item?.id || "new"}`}>
      {COLS[type].map(([k, l]) => (
        <label key={k} className="block"><span className="label-lux !text-xs">{l}</span>
          <input className="input-lux !py-2 text-sm" value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} data-testid={`dir-${type}-${item ? item.id : "new"}-${k}`} /></label>
      ))}
      {type === "vehicle" && <span className="hidden md:block" />}
      <div className="flex gap-1">
        <button onClick={save} className={item ? "btn-ghost !px-3 !py-2" : "btn-ink !px-3 !py-2"} data-testid={`dir-${type}-${item ? item.id : "new"}-save`}>{item ? <Save size={14} /> : <Plus size={14} />}</button>
        {item && <button onClick={remove} className="rounded-full p-2 text-red-700 hover:bg-red-50" data-testid={`dir-${type}-${item.id}-delete`}><Trash2 size={15} /></button>}
      </div>
    </div>
  );
};

export default function Directory() {
  const api = useApi();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api("get", "/directory").then(setItems).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  if (!items) return <Loader2 className="mx-auto animate-spin text-[var(--gold)]" />;
  return (
    <div data-testid="directory-page">
      <PageHead title="Lists" text="Save hotels and vehicles (with their driver) once, then fill a family's stay or transfer with one pick." />
      {[["hotel", "Hotels"], ["vehicle", "Vehicles & drivers"]].map(([type, title]) => (
        <div key={type} className="mb-8">
          <h2 className="font-serif-d mb-3 text-2xl">{title}</h2>
          <div className="overflow-hidden rounded-2xl border border-[var(--line)] bg-white">
            {items.filter((i) => i.type === type).map((i) => <Row key={i.id} item={i} type={type} onSaved={load} />)}
            <Row key={`new-${type}-${items.length}`} type={type} onSaved={load} />
          </div>
        </div>
      ))}
    </div>
  );
}
